/**
 * Centralized Stripe Configuration
 * 
 * This file contains all Stripe price IDs used across the application.
 * When switching from test to live mode, update the price IDs in the 'live' section.
 * 
 * How to go live:
 * 1. Create equivalent products/prices in Stripe Live mode
 * 2. Replace the placeholder "LIVE_PRICE_ID_HERE" values with your live price IDs
 * 3. Update STRIPE_SECRET_KEY to sk_live_xxx in Supabase secrets
 * 4. Update VITE_STRIPE_PUBLISHABLE_KEY to pk_live_xxx in your environment
 */

export type StripeEnvironment = 'test' | 'live';
export type UserType = 'buyer' | 'seller';
export type BillingInterval = 'monthly' | 'annual';

interface PriceConfig {
  id: string;
  amount: number;
  name: string;
  interval: 'month' | 'year';
}

interface StripePriceConfig {
  buyer_monthly: PriceConfig;
  buyer_annual: PriceConfig;
  seller_monthly: PriceConfig;
  seller_annual: PriceConfig;
}

export const STRIPE_PRICES: Record<StripeEnvironment, StripePriceConfig> = {
  test: {
    buyer_monthly: {
      id: "price_1SHApAH4x5qKEjJNVO4W1VqP",
      amount: 999,
      name: "Investor Access Monthly",
      interval: "month",
    },
    buyer_annual: {
      id: "price_1SHCaQH4x5qKEjJNqbqzCPoS",
      amount: 9999,
      name: "Investor Access Annual",
      interval: "year",
    },
    seller_monthly: {
      id: "price_1SHCVzH4x5qKEjJNdE8AMWnw",
      amount: 6800,
      name: "Shark Plan Monthly",
      interval: "month",
    },
    seller_annual: {
      id: "price_1SGUVAH4x5qKEjJNWrbdSYKA",
      amount: 68000,
      name: "Shark Plan Annual",
      interval: "year",
    },
  },
  live: {
    buyer_monthly: {
      id: "price_1SHAq7H4x5qKEjJNHx9CQ2U9",
      amount: 999,
      name: "Investor Access Monthly",
      interval: "month",
    },
    buyer_annual: {
      id: "price_1SHCKfH4x5qKEjJNV1LVaU8Z",
      amount: 9999,
      name: "Investor Access Annual",
      interval: "year",
    },
    seller_monthly: {
      id: "price_1URhtWBcw3dSVbdCU4NwToYn",
      amount: 3499,
      name: "Shark Plan Monthly",
      interval: "month",
    },
    seller_annual: {
      id: "price_1URhtyBcw3dSVbdClOCO4pe0",
      amount: 34999,
      name: "Shark Plan Annual",
      interval: "year",
    },
  },
};

/**
 * Auto-detect the current Stripe environment from the publishable key
 */
export const getCurrentEnvironment = (): StripeEnvironment => {
  const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
  return key.startsWith('pk_live_') ? 'live' : 'test';
};

/**
 * Get the price ID for a specific user type and billing interval
 */
export const getPriceId = (userType: UserType, interval: BillingInterval): string => {
  const env = getCurrentEnvironment();
  const key = `${userType}_${interval}` as keyof StripePriceConfig;
  return STRIPE_PRICES[env][key].id;
};

/**
 * Get the full price configuration object
 */
export const getPrice = (userType: UserType, interval: BillingInterval): PriceConfig => {
  const env = getCurrentEnvironment();
  const key = `${userType}_${interval}` as keyof StripePriceConfig;
  return STRIPE_PRICES[env][key];
};

/**
 * Check if live prices have been configured (not using placeholders)
 */
export const areLivePricesConfigured = (): boolean => {
  return !Object.values(STRIPE_PRICES.live).some(p => p.id === "LIVE_PRICE_ID_HERE");
};

/**
 * Get all price IDs for a specific environment (useful for validation)
 */
export const getAllPriceIds = (env: StripeEnvironment): string[] => {
  return Object.values(STRIPE_PRICES[env]).map(p => p.id);
};

/**
 * Format price amount for display
 */
export const formatPrice = (amount: number): string => {
  return `$${(amount / 100).toFixed(2)}`;
};

/**
 * Previous seller prices (cents), shown crossed out next to the current price.
 */
export const SELLER_COMPARE_AT = { monthly: 6800, annual: 68000 } as const;
